// Per-questionnaire progress for the admin company detail page. Each
// questionnaire tab shows its own Progress bar, so we count — within a single
// QuestionnaireGroup — how many of its visible, required questions are
// "complete". Kept pure (no React / no network) so it can be unit-tested
// directly, the same discipline as companyDetailTabs.ts and wizard.ts.

import type { QuestionnaireGroup } from './companyDetailTabs.ts';

// Minimal structural shape of a submission as far as progress counting cares.
// AdminSubmission (and Submission) satisfy this without conversion.
export type ProgressSubmissionLike = {
  status: string;
  answer: unknown;
  submission_files: unknown[];
  submission_adoptions: unknown[];
};

export type QuestionnaireProgress = {
  /** Visible required questions in this questionnaire. */
  totalRequired: number;
  /** Of those, how many are complete (approved, N/A, or otherwise answered). */
  completedRequired: number;
  /** Rounded percentage; 100 when there is nothing required (matches ProgressBar). */
  percent: number;
};

// A required question counts as complete when a reviewer has settled it
// (approved / not applicable) or the company has actually answered it — i.e.
// there is a non-empty answer, an uploaded file, or an adopted document. A
// bare pending submission with no content is treated as still outstanding so
// the bar reflects work the company still owes.
function isComplete(sub: ProgressSubmissionLike | undefined): boolean {
  if (!sub) return false;
  if (sub.status === 'approved' || sub.status === 'not_applicable') return true;
  if (sub.submission_files.length > 0 || sub.submission_adoptions.length > 0) return true;
  const a = sub.answer;
  if (a === null || a === undefined) return false;
  if (typeof a === 'string') return a.trim() !== '';
  if (Array.isArray(a)) return a.length > 0;
  // Objects (e.g. signature payloads) and primitives like booleans/numbers
  // are meaningful answers.
  return true;
}

/**
 * Count completed vs total required questions for one questionnaire tab.
 *
 * Only the questionnaire's visible (per `visibleIds`) required questions are
 * counted; compliance-doc questions never reach `group.sections` (they live in
 * the Compliance Documents tab), so they are naturally excluded here.
 */
export function questionnaireProgress<S extends ProgressSubmissionLike>(
  group: QuestionnaireGroup,
  visibleIds: Set<string>,
  subByQ: ReadonlyMap<string, S>,
): QuestionnaireProgress {
  let totalRequired = 0;
  let completedRequired = 0;
  for (const section of group.sections) {
    for (const question of section.questions) {
      if (!question.required) continue;
      if (!visibleIds.has(question.id)) continue;
      totalRequired += 1;
      if (isComplete(subByQ.get(question.id))) completedRequired += 1;
    }
  }
  const percent =
    totalRequired === 0 ? 100 : Math.round((completedRequired * 100) / totalRequired);
  return { totalRequired, completedRequired, percent };
}
